import {
  setUnauthorizedNotification,
  setStatusChangeForbiddenNotification,
  setGoogleAPIGeolocationErrorNotification,
  setResourceNotFoundNotification,
  setGenericResponseErrorNotification,
  setInvalidVisibilitySettingsNotification,
  setInvalidParamNotification,
} from '../notification'

import {
  UnauthorizedError,
  ResourceNotFoundError,
  StatusChangeForbiddenError,
  GoogleAPIGeolocationError,
  BadRequestError,
  ForbiddenError,
  InvalidStateError,
  BillingAlreadyDefinedError,
  TowingRecordAlreadyCreatedError,
  InvalidCustomerIdentifierError,
  InvalidVisibilitySettingsError,
  InvalidParameterError,
  ConcurrencyConflict,
  NetworkError,
  InvalidCredentialsError,
  MultifactorAuthenticationError,
  ShiftError,
  GenericError,
} from '../../containers/errorTypes'
import { dataHasError } from './dataHasError'
import { setGenericErrorNotification } from '../notification/genericResponseErrorNotification'
import { Maybe } from '../../common/types'
import { CombinedGraphQLErrors } from '@apollo/client'

interface ResultNotificationsDefinition {
  data: ErrorsTypes
}

export type ErrorsTypes =
  | UnauthorizedError
  | ResourceNotFoundError
  | StatusChangeForbiddenError
  | GoogleAPIGeolocationError
  | BadRequestError
  | ForbiddenError
  | InvalidCredentialsError
  | InvalidStateError
  | BillingAlreadyDefinedError
  | TowingRecordAlreadyCreatedError
  | InvalidCustomerIdentifierError
  | InvalidVisibilitySettingsError
  | InvalidParameterError
  | ConcurrencyConflict
  | NetworkError
  | ShiftError
  | MultifactorAuthenticationError
  | GenericError

export const setErrorNotifications = (params: ResultNotificationsDefinition) => {
  const { data } = params

  if (data == null) {
    setGenericResponseErrorNotification('BadRequestError')
    return
  }

  if (dataHasError(data, 'UnauthorizedError')) {
    return setUnauthorizedNotification()
  } else if (dataHasError(data, 'ResourceNotFoundError')) {
    return setResourceNotFoundNotification()
  }

  if (dataHasError(data, 'StatusChangeForbiddenError')) {
    return setStatusChangeForbiddenNotification('StatusChangeForbiddenError')
  } else if (dataHasError(data, 'BadRequestError')) {
    return setGenericResponseErrorNotification('BadRequestError')
  } else if (dataHasError(data, 'ForbiddenError')) {
    return setGenericResponseErrorNotification('ForbiddenError')
  } else if (dataHasError(data, 'InvalidStateError')) {
    return setGenericResponseErrorNotification('InvalidStateError')
  } else if (dataHasError(data, 'BillingAlreadyDefinedError')) {
    return setGenericResponseErrorNotification('BillingAlreadyDefinedError')
  } else if (dataHasError(data, 'TowingRecordAlreadyCreatedError')) {
    return setGenericResponseErrorNotification('TowingRecordAlreadyCreatedError')
  } else if (dataHasError(data, 'InvalidCustomerIdentifierError')) {
    return setGenericResponseErrorNotification('InvalidCustomerIdentifierError')
  } else if (dataHasError(data, 'GoogleAPIGeolocationError')) {
    return setGoogleAPIGeolocationErrorNotification()
  } else if (dataHasError(data, 'InvalidVisibilitySettingsError')) {
    return setInvalidVisibilitySettingsNotification()
  } else if (dataHasError(data, 'InvalidParameterError')) {
    return setInvalidParamNotification(data.error.message)
  } else if (dataHasError(data, 'ConcurrencyConflict')) {
    return setGenericResponseErrorNotification('ConcurrencyConflict')
  } else if (dataHasError(data, 'MultifactorAuthenticationError')) {
    return setGenericErrorNotification('Virhe', data.error.message)
  }

  if (process.env.NODE_ENV === 'development') {
    console.error('Unable to map data to error, escalating to fatal error', data)
  }

  // setFatalNullErrorNotification(null)
  // setGenericResponseErrorNotification('FatalError')
  console.error(`Failed to map data to error in setErrorNotifications:  ${data.error.message}`);
  setGenericErrorNotification('Virhe', 'Pahoittelut, jokin meni pieleen. Mikäli ongelma jatkuu ota yhteyttä ');
}

export const setFatalErrorNotification = (error: Error) => {
  const errorMessage = parseErrorMessage(error);
  if (process.env.NODE_ENV === 'development') {
    console.error('Fatal Error:', errorMessage)
  }
  /**
   * Temp hack to ignore MFA errors here
   */
  if (ignoreFatalErrorType(errorMessage)) {
    return
  }
  setGenericErrorNotification('Virhe kyselyssä', errorMessage);
}

const parseErrorMessage = (error: Error): string => {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors[0]?.message ?? error.message;
  }
  if (error.message) {
    return error.message;
  }
  console.error(`Failed to resolve error message in parseErrorMessage: ${error.message}`);
  return 'Vastauksen käsittelyssä tapahtui odottamaton virhe. Mikäli ongelma jatkuu, ota yhteyttä asiakaspalveluun';
}

const ignoreFatalErrorType = (errorMessage: Maybe<string>) => {
  if (errorMessage && errorMessage.indexOf('MultifactorAuthenticationError') !== -1) {
    return true
  }

  return false
}
